import React, { useState, useEffect } from 'react';
import { TrendingUp, IndianRupee, Users, Calendar, Sparkles, Star, Zap, BarChart3, Download, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { jwtDecode } from "jwt-decode";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Reports = ({ projects, employees, milestones }) => {
  const [companyName, setCompanyName] = useState('Company Name');
  const [detailedEmployees, setDetailedEmployees] = useState({});
  // ---------- State for company employees ----------
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(true);

  // ---------- State for company projects and milestones ----------
  const [companyProjects, setCompanyProjects] = useState([]);
  const [companyMilestones, setCompanyMilestones] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [milestonesLoading, setMilestonesLoading] = useState(true);

  // ---------- State for project search and selection ----------
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showProjectSelector, setShowProjectSelector] = useState(false);

  // ---------- State for advanced filters ----------
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [customerNameFilter, setCustomerNameFilter] = useState('all');
  const [projectNameFilter, setProjectNameFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [projectHeadFilter, setProjectHeadFilter] = useState('all');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // ---------- State for custom statuses ----------
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);

  // ---------- State for departments ----------
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);

  // ---------- State for project-tasks ----------
  const [allProjectTasks, setAllProjectTasks] = useState([]);
  const [projectTasksLoading, setProjectTasksLoading] = useState(true);

  // Function to fetch all project-tasks (total milestones count)
  const fetchProjectTasks = async () => {
    try {
      setProjectTasksLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/milestones', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let milestones = data.milestones || data;
        console.log('Reports - Fetched all milestones (before filter):', Array.isArray(milestones) ? milestones.length : 0);

        // Filter milestones based on user role
        let decodedToken;
        try {
          decodedToken = jwtDecode(token);
        } catch (e) {
          console.error('Reports - Error decoding token:', e);
        }

        if (decodedToken) {
          const userRole = decodedToken.role;
          const userId = decodedToken.id || decodedToken._id;
          console.log('Reports - User role for tasks:', userRole, 'User ID:', userId);

          const normalizedRole = userRole ? userRole.toLowerCase() : '';

          if (Array.isArray(milestones) && (normalizedRole === 'employee' || normalizedRole === 'team_member')) {
            milestones = milestones.filter(milestone => {
              const assignedTeamMember = milestone.assignedTeamMember;

              if (!assignedTeamMember) return false;

              // Handle array of assigned members
              if (Array.isArray(assignedTeamMember)) {
                return assignedTeamMember.some(member => {
                  const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                  return String(memberId) === String(userId);
                });
              }

              // Handle single assigned member
              const memberId = typeof assignedTeamMember === 'object' ?
                (assignedTeamMember._id || assignedTeamMember.id) : assignedTeamMember;
              return String(memberId) === String(userId);
            });

            console.log('Reports - Filtered milestones for team member:', milestones.length);
          }
        }

        // Use all milestones for total count
        setAllProjectTasks(Array.isArray(milestones) ? milestones : []);
      } else {
        console.error('Reports - Failed to fetch milestones for project-tasks:', response.status);
        setAllProjectTasks([]);
      }
    } catch (error) {
      console.error('Reports - Error fetching project-tasks:', error);
      setAllProjectTasks([]);
    } finally {
      setProjectTasksLoading(false);
    }
  };

  // Function to fetch custom statuses from API
  const fetchCustomStatuses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/prjCustomStatus/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const statuses = await response.json();
        console.log('Reports - API Response:', statuses);
        console.log('Reports - Response type:', typeof statuses);
        console.log('Reports - Is array:', Array.isArray(statuses));

        // Handle wrapped response format {statuses: Array}
        let statusArray = [];
        if (Array.isArray(statuses)) {
          statusArray = statuses;
        } else if (statuses && statuses.statuses && Array.isArray(statuses.statuses)) {
          console.log('Reports - Found wrapped statuses array');
          statusArray = statuses.statuses;
        } else {
          console.log('Reports - Invalid response format:', statuses);
          setStatusOptions([]);
          setStatusLoading(false);
          return;
        }

        if (statusArray.length > 0) {
          console.log('Reports - First status object:', statusArray[0]);
          console.log('Reports - First status keys:', Object.keys(statusArray[0]));

          // Extract status names from the array
          const statusNames = statusArray.map(status => {
            console.log('Reports - Processing status:', status);
            // Try to get the name field
            if (status && typeof status === 'object' && status.name) {
              console.log('Reports - Found name:', status.name);
              return status.name;
            }
            console.log('Reports - No name field found');
            return null;
          }).filter(name => name); // Remove null values

          console.log('Reports - Extracted status names:', statusNames);
          setStatusOptions(statusNames);
        } else {
          console.log('Reports - Empty status array');
          setStatusOptions([]);
        }
      } else {
        console.error('Reports - API Error:', response.status);
        setStatusOptions([]);
      }
    } catch (error) {
      console.error('Reports - Fetch Error:', error);
      setStatusOptions([]);
    } finally {
      setStatusLoading(false);
    }
  };


  // Fetch company details
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          const decodedToken = jwtDecode(token);
          const companyId = decodedToken.role === 'company'
            ? (decodedToken.companyId || decodedToken.id)
            : decodedToken.companyId;

          if (companyId) {
            const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/companies/${companyId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const companyData = await response.json();
              setCompanyName(companyData.businessName || 'Company Name');
            } else {
              console.error('Failed to fetch company details');
            }
          }
        }
      } catch (error) {
        console.error('Error fetching company details:', error);
      }
    };
    fetchCompanyDetails();
    fetchCompanyEmployees();
    fetchCompanyProjects();
    fetchCompanyMilestones();
    fetchCustomStatuses();
    fetchProjectTasks();
  }, [projects, projectsLoading]);

  // ---------------- Fetch Departments (same as AddProject.jsx) ----------------
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem('authToken');

        // Get company ID from token
        let COMPANY_ID = null;
        if (token) {
          try {
            const decodedToken = jwtDecode(token);
            console.log("Reports - Decoded token:", decodedToken);
            COMPANY_ID = decodedToken.companyId || decodedToken.id;
            console.log("Reports - Extracted COMPANY_ID:", COMPANY_ID);
          } catch (error) {
            console.error('Reports - Error decoding token:', error);
          }
        } else {
          console.log("Reports - No token found");
        }

        console.log("Reports - Fetching departments for company:", COMPANY_ID);

        let departmentData = null;

        // First try: with company ID and auth
        if (COMPANY_ID) {
          try {
            const res = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${COMPANY_ID}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            if (res.ok) {
              departmentData = await res.json();
              console.log("Reports - Departments fetched with auth:", departmentData?.length);
              console.log("Reports - Auth response data:", departmentData);
            } else {
              console.log("Reports - Auth fetch failed with status:", res.status, res.statusText);
            }
          } catch (error) {
            console.log("Reports - Auth department fetch failed:", error.message);
          }
        }

        // Second try: without auth for company access
        if (!departmentData && COMPANY_ID) {
          try {
            const res = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${COMPANY_ID}`);
            if (res.ok) {
              departmentData = await res.json();
              console.log("Reports - Departments fetched without auth:", departmentData?.length);
              console.log("Reports - No-auth response data:", departmentData);
            } else {
              console.log("Reports - No-auth fetch failed with status:", res.status, res.statusText);
            }
          } catch (error) {
            console.log("Reports - No-auth department fetch failed:", error.message);
          }
        }

        // Third try: general departments endpoint
        if (!departmentData) {
          try {
            const res = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/departments', {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            if (res.ok) {
              departmentData = await res.json();
              console.log("Reports - General departments fetched:", departmentData?.length);
              console.log("Reports - General response data:", departmentData);
            } else {
              console.log("Reports - General fetch failed with status:", res.status, res.statusText);
            }
          } catch (error) {
            console.log("Reports - General department fetch failed:", error.message);
          }
        }

        setDepartments(departmentData || []);
        console.log("Reports - Final departments set:", departmentData?.length || 0);
        console.log("Reports - Departments data:", departmentData);
      } catch (error) {
        console.error('Reports - Error fetching departments:', error);
        setDepartments([]);
      } finally {
        setDepartmentsLoading(false);
      }
    };

    fetchDepartments();
  }, []);

  // Reset other filters when department changes (Cascading Effect)
  useEffect(() => {
    if (departmentFilter !== 'all') {
      console.log('Reports - Department changed, resetting other filters');
      setCustomerNameFilter('all');
      setProjectNameFilter('all');
      setCityFilter('all');
      setStateFilter('all');
      setProjectHeadFilter('all');
      setProjectStatusFilter('all');
    }
  }, [departmentFilter]);

  // Fetch milestones after projects are loaded
  useEffect(() => {
    if (!projectsLoading) {
      fetchCompanyMilestones();
    }
  }, [projectsLoading]);

  // Fetch detailed employee information
  const fetchEmployeeDetails = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token || !projects) {
        console.log('No token or projects available');
        return;
      }

      console.log('Projects data:', projects);
      const employeeIds = new Set();

      // Collect all unique employee IDs from projects
      projects.forEach(project => {
        console.log('Project teamMembers:', project.teamMembers);
        if (project.teamMembers && Array.isArray(project.teamMembers)) {
          project.teamMembers.forEach(memberId => {
            if (typeof memberId === 'string') {
              employeeIds.add(memberId);
              console.log('Added employee ID:', memberId);
            } else if (typeof memberId === 'object' && memberId._id) {
              employeeIds.add(memberId._id);
              console.log('Added employee ID from object:', memberId._id);
            }
          });
        }
      });

      console.log('Employee IDs to fetch:', Array.from(employeeIds));

      if (employeeIds.size === 0) {
        console.log('No employee IDs found');
        return;
      }

      // Fetch details for each employee
      const employeePromises = Array.from(employeeIds).map(async (employeeId) => {
        try {
          console.log(`Fetching employee: ${employeeId}`);
          const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/employees/${employeeId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          console.log(`Response for ${employeeId}:`, response.status);

          if (response.ok) {
            const employeeData = await response.json();
            console.log(`Employee data for ${employeeId}:`, employeeData);
            return { id: employeeId, data: employeeData };
          } else {
            console.error(`Failed to fetch employee ${employeeId}:`, response.status, response.statusText);
          }
        } catch (error) {
          console.error(`Error fetching employee ${employeeId}:`, error);
        }
        return { id: employeeId, data: null };
      });

      const employeeResults = await Promise.all(employeePromises);
      const employeeMap = {};

      employeeResults.forEach(result => {
        if (result.data) {
          employeeMap[result.id] = result.data;
        }
      });

      console.log('Final employee map:', employeeMap);
      setDetailedEmployees(employeeMap);
    } catch (error) {
      console.error('Error fetching employee details:', error);
    }
  };

  // ---------- Fetch company employees ----------
  const fetchCompanyEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setEmployeeLoading(false);
        return;
      }

      const decodedToken = jwtDecode(token);
      const COMPANY_ID = decodedToken.role === 'company'
        ? (decodedToken.companyId || decodedToken.id)
        : decodedToken.companyId;

      const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/employees/company/${COMPANY_ID}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const employeesData = await response.json();
        const allEmployees = Array.isArray(employeesData) ? employeesData : employeesData.data || [];

        // Filter to show only active employees
        const activeEmployees = allEmployees.filter(emp =>
          emp.isActive !== false && emp.status !== 'Inactive' && emp.status !== 'Deactive'
        );

        console.log('Reports - Total employees:', allEmployees.length, 'Active:', activeEmployees.length);
        setCompanyEmployees(activeEmployees);
      } else {
        console.error('Failed to fetch company employees:', response.status, response.statusText);
        setCompanyEmployees([]);
      }
    } catch (error) {
      console.error('Error fetching company employees:', error);
      setCompanyEmployees([]);
    } finally {
      setEmployeeLoading(false);
    }
  };

  // ---------- Fetch company projects ----------
  const fetchCompanyProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setProjectsLoading(false);
        return;
      }

      const decodedToken = jwtDecode(token);
      const userRole = decodedToken.role;
      const userId = decodedToken.id || decodedToken._id;
      const COMPANY_ID = decodedToken.role === 'company'
        ? (decodedToken.companyId || decodedToken.id)
        : decodedToken.companyId;

      console.log('Reports - Fetching projects for company:', COMPANY_ID);
      console.log('Reports - User role:', userRole, 'User ID:', userId);

      const response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/projectsmgnt`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const projectsData = await response.json();
        let allProjects = Array.isArray(projectsData) ? projectsData : projectsData.data || [];
        console.log('Reports - All projects fetched:', allProjects.length);

        let filteredProjects = allProjects.filter(project =>
          project.companyId === COMPANY_ID || project.company === COMPANY_ID
        );
        console.log('Reports - Filtered projects for company:', filteredProjects.length);

        // Further filter for team members
        if (userRole === 'employee' || userRole === 'team_member') {
          filteredProjects = filteredProjects.filter(project => {
            const isTeamMember = project.teamMembers && Array.isArray(project.teamMembers) &&
              project.teamMembers.some(member => {
                const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                return String(memberId) === String(userId);
              });

            console.log(`Reports - Project "${project.title || project.name}" - isTeamMember:`, isTeamMember);
            return isTeamMember;
          });

          console.log('Reports - Filtered projects for team member:', filteredProjects.length);
        }

        setCompanyProjects(filteredProjects);
      } else {
        console.error('Failed to fetch company projects:', response.status, response.statusText);
        setCompanyProjects([]);
      }
    } catch (error) {
      console.error('Error fetching company projects:', error);
      setCompanyProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  // ---------- Fetch company milestones ----------
  const fetchCompanyMilestones = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setMilestonesLoading(false);
        return;
      }

      const decodedToken = jwtDecode(token);
      const COMPANY_ID = decodedToken.role === 'company'
        ? (decodedToken.companyId || decodedToken.id)
        : decodedToken.companyId;

      console.log('Reports - Fetching milestones for company:', COMPANY_ID);

      // Try without authentication first (like GanttChart does)
      let response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/milestones`);

      // If that fails, try with authentication
      if (!response.ok) {
        console.log('Reports - Trying with authentication...');
        response = await fetch(`https://slateblue-mallard-430836.hostingersite.com/api/milestones`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }

      if (response.ok) {
        const milestonesData = await response.json();
        const allMilestones = Array.isArray(milestonesData) ? milestonesData : milestonesData.data || [];
        console.log('Reports - All milestones fetched:', allMilestones.length);

        // Filter milestones that belong to company projects
        const companyProjectIds = companyProjects.map(p => String(p._id || p.id));
        console.log('Reports - Company project IDs:', companyProjectIds);
        console.log('Reports - Sample milestone structure:', allMilestones[0]);

        // Try filtering by project IDs first
        let filteredMilestones = allMilestones.filter(milestone => {
          const milestoneProjectId = String(milestone.projectId || milestone.project || milestone.projectID);
          const belongs = companyProjectIds.includes(milestoneProjectId);
          if (belongs) {
            console.log('Reports - Milestone belongs to company via project:', milestone.title || milestone.name, 'Project ID:', milestoneProjectId);
          }
          return belongs;
        });

        // If no milestones found by project filtering, try filtering by company ID directly
        if (filteredMilestones.length === 0 && companyProjectIds.length > 0) {
          console.log('Reports - No milestones found by project filtering, trying company ID filtering...');
          filteredMilestones = allMilestones.filter(milestone => {
            const milestoneCompanyId = String(milestone.companyId || milestone.company);
            const belongs = milestoneCompanyId === String(COMPANY_ID);
            if (belongs) {
              console.log('Reports - Milestone belongs to company directly:', milestone.title || milestone.name, 'Company ID:', milestoneCompanyId);
            }
            return belongs;
          });
        }

        // If still no milestones and we have no projects, just show all milestones for debugging
        if (filteredMilestones.length === 0 && companyProjectIds.length === 0) {
          console.log('Reports - No company projects found, showing all milestones for debugging');
          filteredMilestones = allMilestones.slice(0, 5); // Show first 5 for debugging
        }

        console.log('Reports - Final filtered milestones for company:', filteredMilestones.length);
        setCompanyMilestones(filteredMilestones);
      } else {
        console.error('Failed to fetch company milestones:', response.status, response.statusText);
        setCompanyMilestones([]);
      }
    } catch (error) {
      console.error('Error fetching company milestones:', error);
      setCompanyMilestones([]);
    } finally {
      setMilestonesLoading(false);
    }
  };

  // ---------- Cascading Filter Options (Department-based) ----------
  const getFilteredFilterOptions = () => {
    // Helper function to safely extract string values from potentially object fields
    const extractStringValue = (value, fallback = 'Unknown') => {
      if (!value) return fallback;
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        return value.name || value.title || value.teamMemberName || fallback;
      }
      return String(value);
    };

    // Helper function to check if project matches selected department
    const projectMatchesDepartment = (project) => {
      if (departmentFilter === 'all') return true;

      const projectDepartments = [
        project.department,
        project.dept,
        project.departmentName,
        project.category,
        project.projectType
      ];

      for (const dept of projectDepartments) {
        if (!dept) continue;

        if (Array.isArray(dept)) {
          const match = dept.some(d => {
            if (typeof d === 'object') {
              return String(d._id || d.id) === String(departmentFilter);
            }
            return String(d) === String(departmentFilter);
          });
          if (match) return true;
        }
        else if (typeof dept === 'object' && dept !== null) {
          if (String(dept._id || dept.id) === String(departmentFilter)) return true;
        }
        else if (typeof dept === 'string') {
          if (String(dept) === String(departmentFilter)) return true;

          const matchingDept = departments.find(d => d.name === String(dept));
          if (matchingDept && String(matchingDept._id || matchingDept.id) === String(departmentFilter)) {
            return true;
          }
        }
      }
      return false;
    };

    // Filter projects based on selected department
    const departmentFilteredProjects = companyProjects.filter(projectMatchesDepartment);

    console.log(`Reports - Department '${departmentFilter}' filtered projects:`, departmentFilteredProjects.length, 'out of', companyProjects.length);

    // Extract options only from department-filtered projects
    const customerNames = [...new Set(departmentFilteredProjects.map(p => {
      const customer = p.customerName || p.clientName || p.client || p.customer;
      return extractStringValue(customer);
    }).filter(v => v !== 'Unknown'))];

    const projectNames = [...new Set(departmentFilteredProjects.map(p => extractStringValue(p.title || p.name, 'Untitled')).filter(v => v !== 'Untitled'))];

    const cities = [...new Set(departmentFilteredProjects.map(p => {
      const city = p.clientCity || p.city || p.location?.city || p.projectLocation || p.address?.city ||
        p.clientAddress?.city || p.customerCity || p.projectCity;
      return extractStringValue(city);
    }).filter(v => v !== 'Unknown'))];

    const states = [...new Set(departmentFilteredProjects.map(p => {
      const state = p.clientState || p.state || p.location?.state || p.address?.state ||
        p.clientAddress?.state || p.customerState || p.projectState;
      return extractStringValue(state);
    }).filter(v => v !== 'Unknown'))];

    const projectHeads = [...new Set(departmentFilteredProjects.map(p => {
      const head = p.projectHead || p.projectManager || p.manager || p.lead;
      return extractStringValue(head);
    }).filter(v => v !== 'Unknown'))];

    const projectStatuses = [...new Set(departmentFilteredProjects.map(p => extractStringValue(p.status)).filter(v => v !== 'Unknown'))];

    // Debug: Log cascading filter results
    console.log('Reports - Cascading Filter Options (Department-based):', {
      departmentFilter: departmentFilter,
      filteredProjects: departmentFilteredProjects.length,
      customerNames: customerNames.length,
      projectNames: projectNames.length,
      cities: cities.length,
      states: states.length,
      projectHeads: projectHeads.length,
      projectStatuses: projectStatuses.length
    });

    return {
      departments: [], // Will be populated from departments API
      customerNames: customerNames.sort(),
      projectNames: projectNames.sort(),
      cities: cities.sort(),
      states: states.sort(),
      projectHeads: projectHeads.sort(),
      projectStatuses: projectStatuses.sort()
    };
  };

  const filterOptions = getFilteredFilterOptions();

  // ---------- Advanced Filtering Logic ----------
  const getFilteredProjects = () => {
    // Helper function to safely extract string values for filtering
    const extractStringValue = (value, fallback = 'Unknown') => {
      if (!value) return fallback;
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        return value.name || value.title || value.teamMemberName || fallback;
      }
      return String(value);
    };

    return companyProjects.filter(project => {
      // Debug: Log project department data for first few projects
      if (companyProjects.indexOf(project) < 3) {
        console.log(`Reports - Project ${project.title || project.name} department data:`, {
          department: project.department,
          dept: project.dept,
          departmentName: project.departmentName,
          category: project.category,
          projectType: project.projectType
        });
        console.log(`Reports - Current departmentFilter:`, departmentFilter);
        console.log(`Reports - Available departments:`, departments);
      }

      // Enhanced Search filter - searches through all Advanced Filter fields
      const searchMatch = !searchTerm || (() => {
        const searchLower = searchTerm.toLowerCase();

        // Project basic info
        const projectTitle = extractStringValue(project.title || project.name, '');
        const projectStatus = extractStringValue(project.status, '');

        // Department fields
        const departmentFields = [
          project.department,
          project.dept,
          project.departmentName,
          project.category,
          project.projectType
        ].map(field => extractStringValue(field, '')).join(' ');

        // Customer fields
        const customerFields = [
          project.customerName,
          project.clientName,
          project.client,
          project.customer
        ].map(field => extractStringValue(field, '')).join(' ');

        // City fields
        const cityFields = [
          project.clientCity,
          project.city,
          project.location?.city,
          project.projectLocation,
          project.address?.city,
          project.clientAddress?.city,
          project.customerCity,
          project.projectCity
        ].map(field => extractStringValue(field, '')).join(' ');

        // State fields
        const stateFields = [
          project.clientState,
          project.state,
          project.location?.state,
          project.address?.state,
          project.clientAddress?.state,
          project.customerState,
          project.projectState
        ].map(field => extractStringValue(field, '')).join(' ');

        // Project Head fields
        const projectHeadFields = [
          project.projectHead,
          project.projectManager,
          project.manager,
          project.lead
        ].map(field => extractStringValue(field, '')).join(' ');

        // Combine all searchable text
        const allSearchableText = [
          projectTitle,
          projectStatus,
          departmentFields,
          customerFields,
          cityFields,
          stateFields,
          projectHeadFields
        ].join(' ').toLowerCase();

        return allSearchableText.includes(searchLower);
      })();

      // Advanced filters - Simplified department matching
      let departmentMatch = departmentFilter === 'all';

      if (!departmentMatch) {
        // Check all possible department fields
        const projectDepartments = [
          project.department,
          project.dept,
          project.departmentName,
          project.category,
          project.projectType
        ];

        for (const dept of projectDepartments) {
          if (!dept) continue;

          // If dept is an array (multiple departments)
          if (Array.isArray(dept)) {
            departmentMatch = dept.some(d => {
              if (typeof d === 'object') {
                return String(d._id || d.id) === String(departmentFilter);
              }
              return String(d) === String(departmentFilter);
            });
          }
          // If dept is an object with _id or id
          else if (typeof dept === 'object' && dept !== null) {
            departmentMatch = String(dept._id || dept.id) === String(departmentFilter);
          }
          // If dept is a string - check both ID and name matching
          else if (typeof dept === 'string') {
            // Direct ID match
            departmentMatch = String(dept) === String(departmentFilter);

            // If no direct match, check if it's a department name that matches our filter ID
            if (!departmentMatch) {
              const matchingDept = departments.find(d => d.name === String(dept));
              if (matchingDept) {
                departmentMatch = String(matchingDept._id || matchingDept.id) === String(departmentFilter);
              }
            }
          }

          if (departmentMatch) break; // Found a match, no need to check other fields
        }
      }
      const customerMatch = customerNameFilter === 'all' ||
        [project.customerName, project.clientName, project.client, project.customer]
          .map(customer => extractStringValue(customer))
          .includes(customerNameFilter);
      const projectNameMatch = projectNameFilter === 'all' ||
        extractStringValue(project.title || project.name, 'Untitled') === projectNameFilter;
      const cityMatch = cityFilter === 'all' ||
        [project.clientCity, project.city, project.location?.city, project.projectLocation, project.address?.city,
        project.clientAddress?.city, project.customerCity, project.projectCity]
          .map(city => extractStringValue(city))
          .includes(cityFilter);
      const stateMatch = stateFilter === 'all' ||
        [project.clientState, project.state, project.location?.state, project.address?.state,
        project.clientAddress?.state, project.customerState, project.projectState]
          .map(state => extractStringValue(state))
          .includes(stateFilter);
      const projectHeadMatch = projectHeadFilter === 'all' ||
        [project.projectHead, project.projectManager, project.manager, project.lead]
          .map(head => extractStringValue(head))
          .includes(projectHeadFilter);
      const statusMatch = projectStatusFilter === 'all' ||
        extractStringValue(project.status) === projectStatusFilter;

      const finalMatch = searchMatch && departmentMatch && customerMatch && projectNameMatch &&
        cityMatch && stateMatch && projectHeadMatch && statusMatch;

      // Debug: Log filtering results for first few projects
      if (companyProjects.indexOf(project) < 3) {
        console.log(`Reports - Project ${project.title || project.name} filtering:`, {
          searchMatch,
          departmentMatch,
          customerMatch,
          projectNameMatch,
          cityMatch,
          stateMatch,
          projectHeadMatch,
          statusMatch,
          finalMatch
        });
      }

      return finalMatch;
    });
  };

  // ---------- Project Search and Selection Logic ----------
  const filteredProjectsForSearch = getFilteredProjects();

  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProjects.length === filteredProjectsForSearch.length) {
      setSelectedProjects([]);
    } else {
      const allFilteredIds = filteredProjectsForSearch.map(p => p._id || p.id);
      setSelectedProjects(allFilteredIds);
    }
  };

  const clearSelection = () => {
    setSelectedProjects([]);
    setSearchTerm('');
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('all');
    setCustomerNameFilter('all');
    setProjectNameFilter('all');
    setCityFilter('all');
    setStateFilter('all');
    setProjectHeadFilter('all');
    setProjectStatusFilter('all');
  };

  // Filter data based on Advanced Filters and selected projects for reports
  const advancedFilteredProjects = getFilteredProjects();
  const filteredCompanyProjects = selectedProjects.length > 0
    ? advancedFilteredProjects.filter(project => selectedProjects.includes(project._id || project.id))
    : advancedFilteredProjects;

  const filteredCompanyMilestones = selectedProjects.length > 0
    ? companyMilestones.filter(milestone => {
      const milestoneProjectId = String(milestone.projectId || milestone.project || milestone.projectID);
      return selectedProjects.includes(milestoneProjectId);
    })
    : companyMilestones;

  // ---------- Modern Glassmorphism Styles ----------
  const reportsStyle = {
    padding: '2rem',
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
    overflow: 'hidden',
  };

  const backgroundOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    zIndex: 0,
  };

  const contentWrapperStyle = {
    position: 'relative',
    zIndex: 1,
  };

  const pageHeaderStyle = {
    marginBottom: '3rem',
    textAlign: 'center',
    padding: '2rem',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(25px)',
    borderRadius: '2rem',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    animation: 'slideInDown 0.8s ease-out',
  };

  const headerTitleStyle = {
    fontSize: '1.3rem',
    fontWeight: 800,
    marginBottom: '0.5rem',
    position: 'relative',
    zIndex: 2,
    letterSpacing: '1px',
    margin: 0
  };

  const headerSubtitleStyle = {
    fontSize: '1.1rem',
    fontWeight: 500,
    position: 'relative',
    zIndex: 2,
    color: '#6b7280',
    margin: 0
  };

  const headerIconsStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    marginTop: '1.5rem',
    position: 'relative',
    zIndex: 2,
  };

  const metricsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem'
  };

  // ---------- CSS Keyframes ----------
  const keyframesStyle = `
    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    
    @keyframes slideInDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes cardSlideIn {
      from {
        opacity: 0;
        transform: translateX(-20px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateX(0) scale(1);
      }
    }
    
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
  `;

  const metricCardStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    padding: '1rem 1.25rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    transition: 'all 0.2s ease',
  };

  const metricCardHoverStyle = {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  };

  const metricIconStyle = {
    display: 'inline-flex',
    padding: '0.75rem',
    backgroundColor: '#3b82f6',
    borderRadius: '0.5rem',
    color: 'white',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #2563eb',
    position: 'relative',
    zIndex: 2,
  };

  const metricContentStyle = {
    flex: 1,
    minWidth: 0,
    overflow: 'visible'
  };

  const metricTitleStyle = {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#1f2937',
    position: 'relative',
    zIndex: 2,
    margin: '0 0 0.2rem 0'
  };

  const metricValueStyle = {
    fontSize: '1.6rem',
    fontWeight: 900,
    color: '#1f2937',
    position: 'relative',
    zIndex: 2,
    margin: '0 0 0.2rem 0',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    maxWidth: '100%',
    lineHeight: 1.1
  };

  const metricLabelStyle = {
    display: 'flex',
    gap: '1.5rem',
    fontSize: '0.75rem',
    color: '#6b7280',
    position: 'relative',
    zIndex: 2,
    margin: 0
  };

  const chartsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '2rem',
    margin: '2rem 0'
  };

  const chartContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    padding: '2rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    zIndex: 2,
  };

  const chartTitleStyle = {
    fontSize: '1.6rem',
    fontWeight: 800,
    color: '#1f2937',
    margin: 0,
    position: 'relative',
    zIndex: 2,
    marginBottom: '2rem'
  };

  const chartWrapperStyle = {
    height: '300px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };




  const projectDetailsStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    padding: '2rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    zIndex: 2,
  };

  const tableContainerStyle = {
    overflowX: 'auto',
    marginTop: '1rem'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const thStyle = {
    padding: '1rem',
    textAlign: 'left',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    fontWeight: 600,
    color: '#374151'
  };

  const tdStyle = {
    padding: '1rem',
    textAlign: 'left',
    borderBottom: '1px solid #e2e8f0'
  };

  const statusBadgeStyle = {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase'
  };

  const getStatusBadgeStyle = (status) => {
    const baseStyle = { ...statusBadgeStyle };
    switch (status) {
      case 'Completed':
        return { ...baseStyle, backgroundColor: '#dcfce7', color: '#166534' };
      case 'In Progress':
        return { ...baseStyle, backgroundColor: '#dbeafe', color: '#1e40af' };
      case 'Planning':
        return { ...baseStyle, backgroundColor: '#fef3c7', color: '#92400e' };
      default:
        return { ...baseStyle, backgroundColor: '#f3f4f6', color: '#374151' };
    }
  };

  const progressBarStyle = {
    width: '100%',
    height: '20px',
    backgroundColor: '#e5e7eb',
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative'
  };

  const progressFillStyle = {
    height: '100%',
    background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
    transition: 'width 0.3s ease'
  };

  const progressTextStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
  };

  const exportButtonStyle = {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    padding: '0.6rem 1.1rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s ease',
    zIndex: 10
  };

  const exportButtonHoverStyle = {
    backgroundColor: '#2563eb',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)'
  };
  // Calculate project status distribution from filtered projects using dynamic statuses
  const getProjectStatusData = () => {
    if (statusLoading || statusOptions.length === 0) {
      // Fallback to default statuses while loading or if no custom statuses
      return {
        labels: ['Planning', 'In Progress', 'Completed'],
        datasets: [{
          data: [
            filteredCompanyProjects.filter(p => p.status === 'Planning').length,
            filteredCompanyProjects.filter(p => p.status === 'In Progress').length,
            filteredCompanyProjects.filter(p => p.status === 'Completed').length
          ],
          backgroundColor: [
            '#f59e0b', // amber for Planning
            '#D4AFD0', // light pink for In Progress  
            '#10b981'  // emerald green for Completed
          ],
          borderWidth: 0
        }]
      };
    }

    // Use dynamic statuses from API
    const statusCounts = statusOptions.map(status =>
      filteredCompanyProjects.filter(p => p.status === status).length
    );

    // Generate colors for each status
    const generateStatusColors = (count) => {
      const colors = [
        '#f59e0b', // amber - Planning
        '#3b82f6', // blue - Start  
        '#D4AFD0', // light pink - In Process
        '#8b5cf6', // violet - About to Complete
        '#10b981', // emerald green - Completed
        '#06b6d4', // cyan
        '#84cc16', // lime
        '#f97316', // orange
        '#ec4899', // pink
        '#6366f1'  // indigo
      ];
      return colors.slice(0, count);
    };

    return {
      labels: statusOptions,
      datasets: [{
        data: statusCounts,
        backgroundColor: generateStatusColors(statusOptions.length),
        borderWidth: 0
      }]
    };
  };

  const projectStatusData = getProjectStatusData();

  // Calculate milestone completion rate from filtered data
  const milestoneData = {
    labels: filteredCompanyProjects.map(p => p.title || p.name),
    datasets: [{
      label: 'Total Milestones',
      data: filteredCompanyProjects.map(p => filteredCompanyMilestones.filter(m => String(m.projectId || m.project) === String(p._id || p.id)).length),
      backgroundColor: '#e5e7eb'
    }, {
      label: 'Completed Milestones',
      data: filteredCompanyProjects.map(p => filteredCompanyMilestones.filter(m => String(m.projectId || m.project) === String(p._id || p.id) && m.status === 'Completed').length),
      backgroundColor: '#10b981'
    }]
  };

  // Calculate budget utilization from filtered projects
  const totalBudget = filteredCompanyProjects.reduce((sum, p) => sum + (p.budget || 0), 0);
  const completedProjects = filteredCompanyProjects.filter(p => p.status === 'Completed');
  const usedBudget = completedProjects.reduce((sum, p) => sum + (p.budget || 0), 0);




  // Calculate progress based on status position in statusOptions array
  const getStatusProgress = (currentStatus, statusOptions) => {
    if (!statusOptions || statusOptions.length === 0) {
      // Fallback to default statuses if no custom statuses available
      const defaultStatuses = ['Planning', 'In Progress', 'Completed'];
      const statusIndex = defaultStatuses.findIndex(status =>
        status.toLowerCase() === (currentStatus || '').toLowerCase()
      );
      const currentPosition = statusIndex >= 0 ? statusIndex + 1 : 1;
      return {
        current: currentPosition,
        total: defaultStatuses.length,
        percentage: Math.round((currentPosition / defaultStatuses.length) * 100)
      };
    }

    const statusIndex = statusOptions.findIndex(status =>
      status.toLowerCase() === (currentStatus || '').toLowerCase()
    );
    const currentPosition = statusIndex >= 0 ? statusIndex + 1 : 1;

    return {
      current: currentPosition,
      total: statusOptions.length,
      percentage: Math.round((currentPosition / statusOptions.length) * 100)
    };
  };

  // ---------- State Management ----------
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredExport, setHoveredExport] = useState(false);

  // ---------- PDF Export Function ----------
  const exportToPDF = async () => {
    // Ensure employee data is loaded before generating PDF
    if (employeeLoading || projectsLoading) {
      alert('Please wait for data to load completely before exporting PDF.');
      return;
    }

    // Check if there are projects to export
    if (filteredCompanyProjects.length === 0) {
      alert('No projects available to export. Please adjust your filters or select projects.');
      return;
    }

    // Direct export without confirmation dialog

    // Try to refresh employee details if needed
    if (Object.keys(detailedEmployees).length === 0 && companyProjects.length > 0) {
      console.log('Refreshing employee details for PDF export...');
      await fetchEmployeeDetails();
      // Give a small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    console.log('PDF Export - Available data:');
    console.log('- Company Projects:', companyProjects.length);
    console.log('- Company Employees:', companyEmployees.length);
    console.log('- Detailed Employees:', Object.keys(detailedEmployees).length);
    console.log('- Props Employees:', employees?.length || 0);

    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString('en-GB'); // DD/MM/YYYY format

    // Function to add proper black border to current page with reduced margins
    const addPageBorder = () => {
      doc.setDrawColor(0, 0, 0); // Black color
      doc.setLineWidth(0.5); // Thinner border line
      doc.rect(5, 5, 200, 287); // Reduced margins - more space for content
    };

    // Add border to first page
    addPageBorder();

    // Professional Header with Company Branding - Reduced height
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, 210, 35, 'F');

    // Company Name as Main Title
    doc.setFontSize(20); // Reduced font size
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, 10, 20); // Reduced left margin

    // Generated date in top right corner
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    const dateText = `Generated: ${currentDate}`;
    const dateWidth = doc.getTextWidth(dateText);
    doc.text(dateText, 200 - dateWidth, 12); // Top right positioning

    // Subtitle
    doc.setFontSize(12); // Reduced font size
    doc.setFont('helvetica', 'normal');
    doc.text('Project Reports & Analytics', 10, 30); // Reduced left margin

    // Summary Metrics Section with Professional Styling - Compact layout
    doc.setFontSize(14); // Reduced font size
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.text('Project Overview', 10, 42); // Further reduced top margin

    // Draw separator line
    doc.setDrawColor(200, 200, 200);
    doc.line(10, 46, 200, 46); // Extended line width

    doc.setFontSize(10); // Reduced font size
    doc.setTextColor(55, 65, 81);
    doc.setFont('helvetica', 'normal');

    // Show filter information
    const activeFilters = [];
    if (departmentFilter !== 'all') {
      const dept = departments.find(d => (d._id || d.id) === departmentFilter);
      activeFilters.push(`Department: ${dept?.name || departmentFilter}`);
    }
    if (customerNameFilter !== 'all') activeFilters.push(`Customer: ${customerNameFilter}`);
    if (cityFilter !== 'all') activeFilters.push(`City: ${cityFilter}`);
    if (stateFilter !== 'all') activeFilters.push(`State: ${stateFilter}`);
    if (projectStatusFilter !== 'all') activeFilters.push(`Status: ${projectStatusFilter}`);
    if (searchTerm) activeFilters.push(`Search: "${searchTerm}"`);

    // Compact layout - side by side
    const projectCountText = selectedProjects.length > 0
      ? `Selected Projects: ${filteredCompanyProjects.length} of ${advancedFilteredProjects.length} filtered`
      : `Filtered Projects: ${filteredCompanyProjects.length} of ${companyProjects.length}`;

    doc.text(projectCountText, 10, 55);
    doc.text(`Total Budget: Rs. ${totalBudget.toLocaleString('en-IN')}`, 110, 55);

    // Show selection status and active filters
    let yPosition = 62;
    if (selectedProjects.length > 0) {
      doc.setFontSize(8);
      doc.setTextColor(34, 197, 94); // Green color for selection
      doc.text(`✅Exporting ${selectedProjects.length} manually selected projects`, 10, yPosition);
      yPosition += 5;
    }

    if (activeFilters.length > 0) {
      doc.setFontSize(8);
      doc.setTextColor(75, 85, 99);
      doc.text(`Filters Applied: ${activeFilters.join(', ')}`, 10, yPosition);
    }

    // Project Details Table with Team Member Names (filtered projects)
    const projectTableData = filteredCompanyProjects.map(project => {
      const progressData = getStatusProgress(project.status, statusOptions);
      const progress = progressData.percentage;
      const teamMembers = getProjectTeamMembers(project);
      const teamSize = teamMembers.length;

      // Enhanced team details with better formatting
      let teamDetails = 'No team assigned';
      if (teamMembers.length > 0) {
        const memberList = teamMembers.map((member, index) => {
          const name = member.name || member.teamMemberName || `Employee ${index + 1}`;
          const role = member.role ? ` (${member.role})` : '';
          return `${index + 1}. ${name}${role}`;
        }).join('\n');

        teamDetails = `Total employees: ${teamSize}\n${memberList}`;

        // If no names were found, show only team count
        if (teamMembers.length === 0 && project.teamMembers && project.teamMembers.length > 0) {
          console.log('No employee names found, showing only team count');
          const actualTeamSize = project.teamMembers.length;
          teamDetails = `Total employees: ${actualTeamSize}\nEmployee names not available`;
        }
      }

      // Format both start and end dates for PDF
      const startDate = project.startDate || project.createdAt ?
        new Date(project.startDate || project.createdAt).toLocaleDateString('en-GB') :
        'Not set';
      const endDate = project.deadline || project.endDate || project.dueDate ?
        new Date(project.deadline || project.endDate || project.dueDate).toLocaleDateString('en-GB') :
        'Not set';
      const deadline = `Start: ${startDate}\nEnd: ${endDate}`;

      // Format status to ensure single line display
      const formatStatus = (status) => {
        switch (status) {
          case 'In Progress': return 'In Progress';
          case 'Completed': return 'Completed';
          case 'Planning': return 'Planning';
          default: return status || 'Unknown';
        }
      };

      // Format project name to ensure proper word breaking
      const formatProjectName = (name) => {
        if (!name) return 'Untitled Project';
        // Replace hyphens with spaces to allow better word breaking
        return name.replace(/-/g, ' ');
      };


      return [
        formatProjectName(project.title || project.name),
        formatStatus(project.status),
        `${progressData.percentage}/100`,
        `Rs. ${(project.budget || 0).toLocaleString('en-IN')}`,
        teamDetails,
        deadline
      ];
    });

    autoTable(doc, {
      startY: 65, // Further reduced start position
      head: [['Project Name', 'Status', 'Progress', 'Budget', 'Team Details', 'Deadline']],
      body: projectTableData,
      theme: 'striped',
      tableWidth: 'auto',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9, // Reduced font size
        cellPadding: 2, // Reduced padding
        valign: 'middle',
        halign: 'center'
      },
      styles: {
        fontSize: 8, // Reduced font size
        cellPadding: 2, // Reduced padding
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        minCellHeight: 12, // Reduced row height
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 35, fontStyle: 'bold', overflow: 'linebreak', cellPadding: 2, valign: 'middle' },
        1: { cellWidth: 25, halign: 'center', overflow: 'hidden', valign: 'middle' },
        2: { cellWidth: 20, halign: 'center', overflow: 'linebreak', valign: 'middle' },
        3: { cellWidth: 28, halign: 'right', overflow: 'linebreak', valign: 'middle' },
        4: { cellWidth: 45, valign: 'top', cellPadding: 1.5, overflow: 'linebreak' }, // Increased width for team details
        5: { cellWidth: 22, halign: 'center', overflow: 'linebreak', valign: 'middle' }
      },
      margin: { left: 8, right: 8, top: 10, bottom: 15 }, // Reduced margins
      didDrawPage: function (data) {
        // Add border to each new page
        addPageBorder();

        // Add footer to each page
        const pageHeight = doc.internal.pageSize.height;

        // Footer separator line
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.line(10, pageHeight - 20, 200, pageHeight - 20); // Extended line width

        // Footer content - more compact
        doc.setFontSize(7); // Smaller font
        doc.setTextColor(107, 114, 128);
        doc.text(`🕒 ${new Date().getFullYear()} ${companyName}. All rights reserved.`, 10, pageHeight - 14);
        doc.text(`Total Projects: ${companyProjects.length}`, 10, pageHeight - 8); // Removed duplicate date
      }
    });

    // Save the PDF with company name
    doc.save(`${companyName}-Project-Reports-${currentDate.replace(/\//g, '-')}.pdf`);
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      }
    }
  };

  const getEmployeeWorkload = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee.projects ? employee.projects.length : 0;
  };

  const getProjectProgress = (project) => {
    // First check if project has a direct progress field
    if (project.progress !== undefined && project.progress !== null) {
      return Math.round(Number(project.progress) || 0);
    }

    // Otherwise calculate from company milestones
    const projectId = project._id || project.id;
    const projectMilestones = companyMilestones.filter(m => String(m.projectId || m.project) === String(projectId));
    const completedMilestones = projectMilestones.filter(m => m.status === 'Completed');
    return projectMilestones.length > 0 ?
      Math.round((completedMilestones.length / projectMilestones.length) * 100) : 0;
  };

  const getProjectTeamSize = (project) => {
    if (!project.teamMembers) return 0;

    // Try to get actual team members first
    const teamMembers = getProjectTeamMembers(project);
    if (teamMembers.length > 0) {
      return teamMembers.length;
    }

    // Fallback to raw team members array length
    return Array.isArray(project.teamMembers) ? project.teamMembers.length : 0;
  };

  const getProjectTeamMembers = (project) => {
    if (!project.teamMembers || !Array.isArray(project.teamMembers)) {
      console.log('No team members for project:', project.title || project.name);
      return [];
    }

    console.log('Getting team members for project:', project.title || project.name, project.teamMembers);

    // Use detailed employee data from API
    return project.teamMembers.map(memberId => {
      let actualId = memberId;

      // Handle both string IDs and object references
      if (typeof memberId === 'object' && memberId !== null) {
        actualId = memberId._id || memberId.id;
        // If it's already a complete object, return it
        if (memberId.teamMemberName || memberId.name) {
          console.log('Using existing object data:', memberId);
          return {
            name: memberId.teamMemberName || memberId.name || 'Unknown',
            teamMemberName: memberId.teamMemberName || memberId.name || 'Unknown',
            email: memberId.email || '',
            department: memberId.department || '',
            role: memberId.role || ''
          };
        }
      }

      if (typeof actualId === 'string') {
        // First try to get from detailed employees (API data)
        const detailedEmployee = detailedEmployees[actualId];
        console.log(`Looking for employee ${actualId}:`, detailedEmployee);

        if (detailedEmployee) {
          console.log('Found detailed employee:', detailedEmployee);
          return {
            name: detailedEmployee.teamMemberName || detailedEmployee.name || detailedEmployee.firstName || 'Unknown',
            teamMemberName: detailedEmployee.teamMemberName || detailedEmployee.name || detailedEmployee.firstName || 'Unknown',
            email: detailedEmployee.email || '',
            department: detailedEmployee.department || '',
            role: detailedEmployee.role || detailedEmployee.position || ''
          };
        }

        // Try to get from company employees
        const companyEmployee = companyEmployees.find(emp => (emp._id || emp.id) === actualId);
        console.log(`Company employee for ${actualId}:`, companyEmployee);

        if (companyEmployee) {
          return {
            name: companyEmployee.teamMemberName || companyEmployee.name || companyEmployee.firstName || 'Unknown',
            teamMemberName: companyEmployee.teamMemberName || companyEmployee.name || companyEmployee.firstName || 'Unknown',
            email: companyEmployee.email || '',
            department: companyEmployee.department || '',
            role: companyEmployee.role || companyEmployee.position || ''
          };
        }

        // Fallback to employees prop
        const employee = employees.find(emp => (emp._id || emp.id) === actualId);
        console.log(`Fallback employee for ${actualId}:`, employee);

        if (employee) {
          return {
            name: employee.teamMemberName || employee.name || employee.firstName || 'Unknown',
            teamMemberName: employee.teamMemberName || employee.name || employee.firstName || 'Unknown',
            email: employee.email || '',
            department: employee.department || '',
            role: employee.role || employee.position || ''
          };
        }
      }

      console.log('No employee data found for:', memberId);
      return {
        name: `Employee ${actualId ? String(actualId).slice(-4) : 'Unknown'}`,
        teamMemberName: `Employee ${actualId ? String(actualId).slice(-4) : 'Unknown'}`,
        email: '',
        department: '',
        role: ''
      };
    }).filter(member => member.name && member.name !== 'Unknown'); // Filter out completely unknown members
  };

  return (
    <>
      <style>{keyframesStyle}</style>
      <div style={reportsStyle}>
        <div style={backgroundOverlayStyle}></div>
        <div style={contentWrapperStyle}>
          {/* Header removed as per request */}

          {/* Project Search and Selection Panel */}
          <div style={{
            marginBottom: '2rem',
            padding: '1.5rem',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            animation: 'fadeInUp 1s ease-out 0.2s both'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                Search & Select Projects
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db'
                }}>
                  {selectedProjects.length} selected
                </span>
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '240px',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.875rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box',
                    height: '38px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
                {selectedProjects.length > 0 && (
                  <button
                    onClick={clearSelection}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      height: '38px'
                    }}
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={() => setShowProjectSelector(!showProjectSelector)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: showProjectSelector ? '#6b7280' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    height: '38px'
                  }}
                >
                  {showProjectSelector ? 'Hide Projects' : 'Select Projects'}
                </button>
                <button
                  onClick={exportToPDF}
                  onMouseEnter={() => setHoveredExport(true)}
                  onMouseLeave={() => setHoveredExport(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                    height: '38px',
                    ...(hoveredExport ? { backgroundColor: '#059669', transform: 'translateY(-1px)' } : {})
                  }}
                >
                  <Download size={18} />
                  {selectedProjects.length > 0
                    ? `Export PDF (${selectedProjects.length})`
                    : `Export PDF (${filteredCompanyProjects.length})`}
                </button>
              </div>
            </div>

            {showProjectSelector && (
              <div>

                {/* Select All Button */}
                <div style={{ marginBottom: '1rem' }}>
                  <button
                    onClick={handleSelectAll}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: selectedProjects.length === filteredProjectsForSearch.length && filteredProjectsForSearch.length > 0 ? '#10b981' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    {selectedProjects.length === filteredProjectsForSearch.length && filteredProjectsForSearch.length > 0
                      ? 'Deselect All'
                      : `Select All (${filteredProjectsForSearch.length})`}
                  </button>
                </div>

                {/* Projects List */}
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: '#f9fafb'
                }}>
                  {filteredProjectsForSearch.length === 0 ? (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      {searchTerm ? 'No projects found matching your search' : 'No projects available'}
                    </div>
                  ) : (
                    filteredProjectsForSearch.map((project) => (
                      <div
                        key={project._id || project.id}
                        style={{
                          padding: '0.75rem 1rem',
                          borderBottom: '1px solid #f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          cursor: 'pointer',
                          backgroundColor: selectedProjects.includes(project._id || project.id) ? '#f0f9ff' : 'transparent',
                          transition: 'background-color 0.2s ease'
                        }}
                        onClick={() => handleProjectToggle(project._id || project.id)}
                        onMouseEnter={(e) => {
                          if (!selectedProjects.includes(project._id || project.id)) {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedProjects.includes(project._id || project.id)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(project._id || project.id)}
                          onChange={() => { }}
                          style={{ cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontWeight: 500,
                            color: selectedProjects.includes(project._id || project.id) ? '#0c4a6e' : '#374151',
                            marginBottom: '0.25rem'
                          }}>
                            {project.title || project.name}
                          </div>
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            display: 'flex',
                            gap: '1rem'
                          }}>
                            <span>Status: {project.status || 'Unknown'}</span>
                            <span>Budget: ₹{(project.budget || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Advanced Filters Section */}
            <div style={{
              marginTop: '0.75rem'
            }}>
              <div style={{
                marginTop: '0.75rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#f8fafc',
                borderRadius: '0.5rem',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: showAdvancedFilters ? '1rem' : '0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                    <h3 style={{
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      color: '#1f2937',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem'
                    }}>
                      <Filter size={16} style={{ color: '#3b82f6' }} />
                      Advanced Filters
                    </h3>
                    {showAdvancedFilters ? <ChevronUp size={16} style={{ color: '#6b7280' }} /> : <ChevronDown size={16} style={{ color: '#6b7280' }} />}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={clearAllFilters}
                      style={{
                        padding: '0.35rem 0.75rem',
                        backgroundColor: '#fee2e2',
                        color: '#ef4444',
                        border: '1px solid #fecaca',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#ef4444';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                        e.currentTarget.style.color = '#ef4444';
                      }}
                    >
                      Clear All Filters
                    </button>
                  </div>
                </div>

                {showAdvancedFilters && (
                  <>
                    <div style={{
                      height: '1px',
                      backgroundColor: '#e5e7eb',
                      marginBottom: '1rem'
                    }}></div>

                    {/* Filter Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem'
                    }}>
                      {/* Department Filter */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          Department
                        </label>
                        <select
                          value={departmentFilter}
                          onChange={(e) => setDepartmentFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        >
                          <option value="all">All Departments</option>
                          {departmentsLoading ? (
                            <option disabled>Loading departments...</option>
                          ) : departments.length === 0 ? (
                            <option disabled>No departments found</option>
                          ) : (
                            departments.map(dept => {
                              console.log("Reports - Rendering department:", dept);
                              return (
                                <option key={dept._id || dept.id} value={dept._id || dept.id}>
                                  {dept.name}
                                </option>
                              );
                            })
                          )}
                        </select>
                      </div>

                      {/* Customer Name Filter */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          Client Name
                        </label>
                        <select
                          value={customerNameFilter}
                          onChange={(e) => setCustomerNameFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        >
                          <option value="all">All Client</option>
                          {filterOptions.customerNames.length === 0 ? (
                            <option disabled>No customers found</option>
                          ) : (
                            filterOptions.customerNames.map(customer => (
                              <option key={customer} value={customer}>{customer}</option>
                            ))
                          )}
                        </select>
                      </div>

                      {/* Project Name Filter */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          Project Name
                        </label>
                        <select
                          value={projectNameFilter}
                          onChange={(e) => setProjectNameFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        >
                          <option value="all">All Projects</option>
                          {filterOptions.projectNames.length === 0 ? (
                            <option disabled>No projects found</option>
                          ) : (
                            filterOptions.projectNames.map(project => (
                              <option key={project} value={project}>{project}</option>
                            ))
                          )}
                        </select>
                      </div>

                      {/* City Filter */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          ™ï City
                        </label>
                        <select
                          value={cityFilter}
                          onChange={(e) => setCityFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        >
                          <option value="all">All Cities</option>
                          {filterOptions.cities.length === 0 ? (
                            <option disabled>No cities found</option>
                          ) : (
                            filterOptions.cities.map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))
                          )}
                        </select>
                      </div>

                      {/* State Filter */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          —ï State
                        </label>
                        <select
                          value={stateFilter}
                          onChange={(e) => setStateFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        >
                          <option value="all">All States</option>
                          {filterOptions.states.length === 0 ? (
                            <option disabled>No states found</option>
                          ) : (
                            filterOptions.states.map(state => (
                              <option key={state} value={state}>{state}</option>
                            ))
                          )}
                        </select>
                      </div>

                      {/* Project Head Filter */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          💼 Project Head
                        </label>
                        <select
                          value={projectHeadFilter}
                          onChange={(e) => setProjectHeadFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        >
                          <option value="all">All Project Heads</option>
                          {filterOptions.projectHeads.length === 0 ? (
                            <option disabled>No project heads found</option>
                          ) : (
                            filterOptions.projectHeads.map(head => (
                              <option key={head} value={head}>{head}</option>
                            ))
                          )}
                        </select>
                      </div>

                      {/* Project Status Filter */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          📊 Project Status
                        </label>
                        <select
                          value={projectStatusFilter}
                          onChange={(e) => setProjectStatusFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            backgroundColor: 'white',
                            outline: 'none'
                          }}
                        >
                          <option value="all">All Statuses</option>
                          {statusLoading ? (
                            <option disabled>Loading statuses...</option>
                          ) : statusOptions.length === 0 ? (
                            <option disabled>No statuses found</option>
                          ) : (
                            statusOptions.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))
                          )}
                        </select>
                      </div>
                    </div>

                    {/* Filter Summary */}
                    <div style={{
                      marginTop: '1.5rem',
                      padding: '0.75rem 1.25rem',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '0.5rem',
                      border: '1px solid #bae6fd',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      animation: 'fadeInUp 0.5s ease-out'
                    }}>
                      <div style={{
                        fontSize: '0.875rem',
                        color: '#0369a1',
                        fontWeight: 600
                      }}>
                        📈 Showing {filteredCompanyProjects.length} of {companyProjects.length} projects
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Key Metrics */}
          <div style={{ ...metricsGridStyle, animation: 'fadeInUp 1s ease-out 0.3s both' }}>
            <div
              style={{
                ...metricCardStyle,
                ...(hoveredCard === 'projects' ? metricCardHoverStyle : {}),
              }}
              onMouseEnter={() => setHoveredCard('projects')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={metricIconStyle}><TrendingUp size={20} /></div>
              <div style={metricContentStyle}>
                <h3 style={metricTitleStyle}>Filtered Projects</h3>
                <div style={metricValueStyle}>
                  {projectsLoading ? '...' : filteredCompanyProjects.length}
                </div>
                <p style={metricLabelStyle}>Projects in report</p>
              </div>
            </div>

            <div
              style={{
                ...metricCardStyle,
                ...(hoveredCard === 'budget' ? metricCardHoverStyle : {}),
              }}
              onMouseEnter={() => setHoveredCard('budget')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={metricIconStyle}><IndianRupee size={20} /></div>
              <div style={metricContentStyle}>
                <h3 style={metricTitleStyle}>Total Budget</h3>
                <div style={{
                  ...metricValueStyle,
                  fontSize: projectsLoading ? '1.6rem' :
                    totalBudget.toLocaleString().length > 10 ? '1.1rem' :
                      totalBudget.toLocaleString().length > 8 ? '1.2rem' :
                        totalBudget.toLocaleString().length > 7 ? '1.3rem' :
                          totalBudget.toLocaleString().length > 6 ? '1.4rem' : '1.6rem'
                }}>{projectsLoading ? '...' : totalBudget.toLocaleString()}</div>
                <p style={metricLabelStyle}>Across all projects</p>
              </div>
            </div>

            <div
              style={{
                ...metricCardStyle,
                ...(hoveredCard === 'employees' ? metricCardHoverStyle : {}),
              }}
              onMouseEnter={() => setHoveredCard('employees')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={metricIconStyle}><Users size={20} /></div>
              <div style={metricContentStyle}>
                <h3 style={metricTitleStyle}>Team Members</h3>
                <div style={metricValueStyle}>
                  {employeeLoading ? '...' : companyEmployees.length}
                </div>
                <p style={metricLabelStyle}>Active employees</p>
              </div>
            </div>

            <div
              style={{
                ...metricCardStyle,
                ...(hoveredCard === 'milestones' ? metricCardHoverStyle : {}),
              }}
              onMouseEnter={() => setHoveredCard('milestones')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={metricIconStyle}><Calendar size={20} /></div>
              <div style={metricContentStyle}>
                <h3 style={metricTitleStyle}>Project-Tasks</h3>
                <div style={metricValueStyle}>
                  {projectTasksLoading ? '...' : allProjectTasks.length}
                </div>
                <p style={metricLabelStyle}>Total Project-Tasks</p>
              </div>
            </div>
          </div>

          {/* Enhanced Charts */}
          <div style={chartsGridStyle}>
            <div style={chartContainerStyle}>
              <h3 style={chartTitleStyle}>📈 Project Status Distribution</h3>
              <div style={chartWrapperStyle}>
                <Doughnut data={projectStatusData} options={chartOptions} />
              </div>
            </div>
          </div>

          {/* Enhanced Project Details Table */}
          <div style={projectDetailsStyle}>
            <h3 style={chartTitleStyle}>
              📋 Project Details
              {selectedProjects.length > 0 && (
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  marginLeft: '1rem',
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db'
                }}>
                  {selectedProjects.length} selected project{selectedProjects.length !== 1 ? 's' : ''}
                </span>
              )}
            </h3>
            {projectsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Loading project details...
              </div>
            ) : filteredCompanyProjects.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                No projects match your current filters
              </div>
            ) : (
              <div style={tableContainerStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Project Name</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Progress</th>
                      <th style={thStyle}>Budget</th>
                      <th style={thStyle}>Team Size</th>
                      <th style={thStyle}>Deadline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanyProjects.map(project => {
                      const progressData = getStatusProgress(project.status, statusOptions);
                      const progress = progressData.percentage;
                      const teamSize = getProjectTeamSize(project);
                      return (
                        <tr key={project._id || project.id}>
                          <td style={tdStyle}>{project.title || project.name}</td>
                          <td style={tdStyle}>
                            <span style={getStatusBadgeStyle(project.status)}>
                              {project.status}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <div style={progressBarStyle}>
                              <div
                                style={{
                                  ...progressFillStyle,
                                  width: `${progress}%`
                                }}
                              ></div>
                              <span style={progressTextStyle}>{progressData.percentage}/100</span>
                            </div>
                          </td>
                          <td style={tdStyle}>
                            ₹{(project.budget || 0).toLocaleString()}
                          </td>
                          <td style={tdStyle}>
                            {teamSize} members
                          </td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {/* Start Date */}
                              <div style={{ fontSize: '0.85rem' }}>
                                <strong>Start:</strong> {project.startDate || project.createdAt ?
                                  new Date(project.startDate || project.createdAt).toLocaleDateString('en-GB') :
                                  'Not set'}
                              </div>
                              {/* End Date */}
                              <div style={{ fontSize: '0.85rem' }}>
                                <strong>End:</strong> {project.deadline || project.endDate || project.dueDate ?
                                  new Date(project.deadline || project.endDate || project.dueDate).toLocaleDateString('en-GB') :
                                  'Not set'}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Reports;

